// components/ItineraryView.jsx
// Day-by-day itinerary view component
// Displays AI-generated itinerary in a beautiful card-based layout

import React, { useState } from 'react';

// Safe string helper
const safeStr = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return value.name || value.city || '';
  return String(value);
};

// Parse itinerary text into structured days
const parseItineraryText = (text) => {
  if (!text) return [];
  
  const days = [];
  const dayPatterns = [
    /Day\s*(\d+)[:\s-]+([^\n]+)/gi,
    /\*\*Day\s*(\d+)[:\s-]*([^\*]+)\*\*/gi,
    /#{1,3}\s*Day\s*(\d+)[:\s-]*([^\n]+)/gi
  ];
  
  // Try to split by "Day X" patterns
  const sections = text.split(/(?=Day\s*\d+)/i);
  
  sections.forEach((section, idx) => {
    if (!section.trim()) return;
    
    const dayMatch = section.match(/Day\s*(\d+)[:\s-]*/i);
    const dayNum = dayMatch ? parseInt(dayMatch[1]) : idx + 1;
    
    // Extract title (first line after "Day X")
    const lines = section.split('\n').filter(l => l.trim());
    let title = '';
    let activities = [];
    
    if (lines.length > 0) {
      // First line is title
      title = lines[0].replace(/Day\s*\d+[:\s-]*/i, '').replace(/[\*#]/g, '').trim();
      
      // Rest are activities
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line && !line.startsWith('---')) {
          // Clean up markdown
          const cleanLine = line
            .replace(/^[-•*]\s*/, '')
            .replace(/\*\*/g, '')
            .replace(/^\d+\.\s*/, '')
            .trim();
          
          if (cleanLine) {
            activities.push(cleanLine);
          }
        }
      }
    }
    
    if (title || activities.length > 0) {
      days.push({
        day: dayNum,
        title: title || `Day ${dayNum}`,
        activities: activities.slice(0, 6) // Limit activities
      });
    }
  });
  
  return days;
};

// Get icon for activity type
const getActivityIcon = (activity) => {
  const text = activity.toLowerCase();
  if (text.includes('breakfast') || text.includes('lunch') || text.includes('dinner') || text.includes('restaurant') || text.includes('food')) return '🍽️';
  if (text.includes('hotel') || text.includes('check-in') || text.includes('check in') || text.includes('resort')) return '🏨';
  if (text.includes('flight') || text.includes('airport') || text.includes('arrive')) return '✈️';
  if (text.includes('beach') || text.includes('swim') || text.includes('snorkel')) return '🏖️';
  if (text.includes('museum') || text.includes('gallery') || text.includes('art')) return '🎨';
  if (text.includes('temple') || text.includes('church') || text.includes('mosque')) return '🛕';
  if (text.includes('shopping') || text.includes('market') || text.includes('mall')) return '🛍️';
  if (text.includes('tour') || text.includes('explore') || text.includes('visit')) return '🚶';
  if (text.includes('safari') || text.includes('wildlife') || text.includes('animal')) return '🦁';
  if (text.includes('spa') || text.includes('massage') || text.includes('relax')) return '💆';
  if (text.includes('sunset') || text.includes('sunrise')) return '🌅';
  if (text.includes('night') || text.includes('club') || text.includes('bar')) return '🌙';
  if (text.includes('cruise') || text.includes('boat') || text.includes('yacht')) return '🚢';
  if (text.includes('hike') || text.includes('trek') || text.includes('mountain')) return '🥾';
  if (text.includes('desert') || text.includes('dune')) return '🏜️';
  if (text.includes('photo') || text.includes('view') || text.includes('scenic')) return '📸';
  return '📍';
};

// Day Card Component
const DayCard = ({ day, isExpanded, onToggle }) => {
  const styles = {
    card: {
      background: '#ffffff',
      borderRadius: '14px',
      border: '2px solid #e5e7eb',
      marginBottom: '12px',
      overflow: 'hidden',
      transition: 'all 0.2s'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      padding: '16px 20px',
      cursor: 'pointer',
      background: isExpanded ? '#fefce8' : 'transparent'
    },
    dayBadge: {
      width: '40px',
      height: '40px',
      borderRadius: '10px',
      background: 'linear-gradient(145deg, #fbbf24, #f59e0b)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#18181b',
      fontWeight: '700',
      fontSize: '14px',
      marginRight: '14px',
      flexShrink: 0
    },
    titleSection: {
      flex: 1
    },
    title: {
      fontSize: '15px',
      fontWeight: '600',
      color: '#18181b',
      marginBottom: '2px'
    },
    subtitle: {
      fontSize: '12px',
      color: '#71717a'
    },
    expandIcon: {
      fontSize: '14px',
      color: '#71717a',
      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s'
    },
    body: {
      padding: isExpanded ? '0 20px 16px 20px' : '0 20px',
      maxHeight: isExpanded ? '500px' : '0',
      overflow: 'hidden',
      transition: 'all 0.3s ease'
    },
    activityList: {
      paddingTop: '8px',
      borderTop: '1px solid #f4f4f5'
    },
    activity: {
      display: 'flex',
      alignItems: 'flex-start',
      padding: '10px 0',
      borderBottom: '1px solid #f9fafb'
    },
    activityIcon: {
      width: '28px',
      height: '28px',
      borderRadius: '8px',
      background: '#f4f4f5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      marginRight: '12px',
      flexShrink: 0
    },
    activityText: {
      fontSize: '13px',
      color: '#3f3f46',
      lineHeight: '1.5'
    }
  };

  return (
    <div style={styles.card}>
      <div style={styles.header} onClick={onToggle}>
        <div style={styles.dayBadge}>D{day.day}</div>
        <div style={styles.titleSection}>
          <div style={styles.title}>{day.title}</div>
          <div style={styles.subtitle}>{day.activities.length} activities planned</div>
        </div>
        <div style={styles.expandIcon}>▼</div>
      </div>
      
      <div style={styles.body}>
        <div style={styles.activityList}>
          {day.activities.map((activity, idx) => (
            <div key={idx} style={styles.activity}>
              <div style={styles.activityIcon}>{getActivityIcon(activity)}</div>
              <div style={styles.activityText}>{activity}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Main Itinerary View Component
const ItineraryView = ({ 
  itineraryData = {},
  message = '',
  days = [], // Pre-parsed days (optional)
  onClose,
  onBookFlights,
  onBookHotels
}) => {
  const [expandedDays, setExpandedDays] = useState({ 0: true, 1: true });
  
  // Parse days from message if not provided
  const parsedDays = days.length > 0 ? days : parseItineraryText(message);
  
  const destination = safeStr(itineraryData.destination) || 'Your Destination';
  const nights = itineraryData.nights || parsedDays.length || 3;
  const travelerType = safeStr(itineraryData.travelerType) || '';
  
  const toggleDay = (index) => {
    setExpandedDays(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const styles = {
    container: {
      background: '#ffffff',
      borderRadius: '16px',
      padding: '24px',
      marginBottom: '24px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '20px'
    },
    titleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '8px'
    },
    titleIcon: {
      width: '48px',
      height: '48px',
      borderRadius: '12px',
      background: 'linear-gradient(145deg, #fef3c7, #fde68a)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '24px'
    },
    title: {
      fontSize: '22px',
      fontWeight: '700',
      color: '#18181b',
      margin: 0
    },
    subtitle: {
      fontSize: '14px',
      color: '#71717a'
    },
    closeBtn: {
      background: '#f4f4f5',
      border: 'none',
      width: '36px',
      height: '36px',
      borderRadius: '8px',
      fontSize: '18px',
      cursor: 'pointer',
      color: '#71717a'
    },
    meta: {
      display: 'flex',
      gap: '16px',
      marginBottom: '20px',
      flexWrap: 'wrap'
    },
    metaItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 14px',
      background: '#f4f4f5',
      borderRadius: '8px',
      fontSize: '13px',
      color: '#3f3f46'
    },
    daysSection: {
      marginBottom: '20px'
    },
    sectionTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#71717a',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      marginBottom: '14px'
    },
    actionBtns: {
      display: 'flex',
      gap: '12px',
      flexWrap: 'wrap'
    },
    primaryBtn: {
      flex: 1,
      minWidth: '150px',
      padding: '14px 20px',
      borderRadius: '10px',
      border: 'none',
      background: 'linear-gradient(145deg, #fbbf24, #f59e0b)',
      color: '#18181b',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    secondaryBtn: {
      flex: 1,
      minWidth: '150px',
      padding: '14px 20px',
      borderRadius: '10px',
      border: '2px solid #e5e7eb',
      background: '#ffffff',
      color: '#3f3f46',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    tip: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      padding: '14px 16px',
      background: '#f0fdf4',
      borderRadius: '10px',
      marginTop: '16px',
      fontSize: '13px',
      color: '#166534'
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px 20px',
      color: '#71717a'
    },
    emptyIcon: {
      fontSize: '48px',
      marginBottom: '12px'
    },
    messageBox: {
      background: '#f9fafb',
      borderRadius: '12px',
      padding: '16px',
      marginBottom: '20px',
      fontSize: '14px',
      color: '#3f3f46',
      lineHeight: '1.6',
      whiteSpace: 'pre-wrap'
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <div style={styles.titleRow}>
            <div style={styles.titleIcon}>📅</div>
            <div>
              <h2 style={styles.title}>{destination} Itinerary</h2>
              <div style={styles.subtitle}>{nights} days of adventure planned for you</div>
            </div>
          </div>
        </div>
        {onClose && (
          <button style={styles.closeBtn} onClick={onClose}>×</button>
        )}
      </div>

      {/* Meta Info */}
      <div style={styles.meta}>
        <div style={styles.metaItem}>
          <span>📆</span>
          <span>{nights} Days</span>
        </div>
        {travelerType && (
          <div style={styles.metaItem}>
            <span>👥</span>
            <span>{travelerType}</span>
          </div>
        )}
        {itineraryData.pace && (
          <div style={styles.metaItem}>
            <span>🚶</span>
            <span>{itineraryData.pace} pace</span>
          </div>
        )}
      </div>

      {/* Days */}
      {parsedDays.length > 0 ? (
        <div style={styles.daysSection}>
          <div style={styles.sectionTitle}>Your Day-by-Day Plan</div>
          {parsedDays.map((day, index) => (
            <DayCard
              key={index}
              day={day}
              isExpanded={!!expandedDays[index]}
              onToggle={() => toggleDay(index)}
            />
          ))}
        </div>
      ) : message ? (
        <div style={styles.messageBox}>{message}</div>
      ) : (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📋</div>
          <div>No itinerary details available yet.</div>
        </div>
      )}

      {/* Action Buttons */}
      <div style={styles.actionBtns}>
        {onBookFlights && (
          <button style={styles.primaryBtn} onClick={onBookFlights}>
            <span>✈️</span>
            <span>Search Flights</span>
          </button>
        )}
        {onBookHotels && (
          <button style={styles.secondaryBtn} onClick={onBookHotels}>
            <span>🏨</span>
            <span>Find Hotels</span>
          </button>
        )}
      </div>

      {/* Tip */}
      <div style={styles.tip}>
        <span>💡</span>
        <span>This itinerary is customizable! Just tell me if you'd like to add, remove, or change any activities.</span>
      </div>
    </div>
  );
};

export default ItineraryView;
